import { IHttpService } from "./ihttp-service";
import { IHttpServiceClient } from "./ihttp-service-client";
import { IHttpServiceOptions } from "./ihttp-service-options";
import { handleHttpError } from '@/utils';
import { tokenInterceptor } from '@/services/shared/http';
import { showToast } from "@/store";

export class HttpService<Options extends IHttpServiceOptions> implements IHttpService<Options> {
  private client: IHttpServiceClient<Options>;

  constructor(client: IHttpServiceClient<Options>) {
    this.client = client;
  }

  private buildQueryString(params: Record<string, any>): string {
    return Object.entries(params)
      .filter(([_, value]) => value !== undefined && value !== null)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&');
  }

  public async get<R = unknown>(url: string, options?: Options, shouldShowToast: boolean = true): Promise<R> {
    try {
    
      const config = this.configure(options);
      const queryString = config.params ? this.buildQueryString(config.params) : '';
      const fullUrl = queryString ? `${url}?${queryString}` : url;

      const result = await this.client.get(fullUrl, config) as R;
      
      if (shouldShowToast) {
        showToast('Richiesta GET riuscita', '', 'success');
      }

      return result;

    } catch (err) {
      throw new Error(await handleHttpError(err, shouldShowToast));
    }
  }

  public async post<R = unknown, B = unknown>(url: string, body: B, options?: Options, shouldShowToast: boolean = true): Promise<R> {
    try {
      const result = await this.client.post<R>(url, body, this.configure(options));
      
      if (shouldShowToast) {
        const message = result && typeof result === 'object' && 
          'data' in result && 
          typeof (result as any).data === 'object' && 
          'message' in (result as any).data
            ? (result as any).data.message 
            : 'Richiesta POST riuscita';
        
        showToast('Sincronizzazione completata 🤩', message , 'success');
      }
      
      return result;
    } catch (err) {
      throw new Error(await handleHttpError(err, shouldShowToast));
    }
  }

  public async put<R = unknown, B = unknown>(url: string, body?: B, options?: Options, shouldShowToast: boolean = true): Promise<R> {
    try {
      const result = await this.client.put<R>(url, body, this.configure(options));

      if (shouldShowToast) {
        const message = result && typeof result === 'object' && 
          'data' in result && 
          typeof (result as any).data === 'object' && 
          'message' in (result as any).data
            ? (result as any).data.message 
            : 'Richiesta POST riuscita';
        
        showToast('Aggiornamento effettuato 🤩', message , 'success');
      }

      return result;

    } catch (err) {
      throw new Error(await handleHttpError(err, shouldShowToast));
    }
  }

  public async patch<R = unknown, B = unknown>(url: string, body?: B, options?: Options, shouldShowToast: boolean = true): Promise<R> {
    try {
      const result = await this.client.patch<R>(url, body, this.configure(options));

      if (shouldShowToast) {
        showToast('Richiesta PATCH riuscita', '', 'success');
      }

      return result;

    } catch (err) {
      throw new Error(await handleHttpError(err, shouldShowToast));
    }
  }

  public async delete<R = unknown, B = unknown>(url: string, body?: B, options?: Options, shouldShowToast: boolean = true): Promise<R> {
    try {
      const result = await this.client.delete<R>(url, body, this.configure(options));

      if (shouldShowToast) {
        showToast('Richiesta DELETE riuscita', '', 'success');
      }

      return result;

    } catch (err) {
      throw new Error(await handleHttpError(err, shouldShowToast));
    }
  }

  private configure(customOptions?: Options): Options & { useToken?: boolean } {
    let options: Options & { useToken?: boolean } = { 
      ...this.client.options,
      timeout: 6000000,
      ...customOptions 
    };
  
    if (options.useToken) {
      const updatedOptions = tokenInterceptor.attachToken(options);
      options = { ...options, ...updatedOptions };
    }
      
    return options;
  }
  

}
